import { isDesktopRuntime } from '../lib/api';

export function getErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function getErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

// §1.11 sudo gate: a sensitive op reached without a fresh (<15min) sudo
// elevation is rejected with a 403 carrying the machine-readable
// `{error:{code:"sudo_required"}}` body (see backend `sudo_guard.rs`). `lib/api.ts`
// lifts that nested `code` onto the thrown `ApiError.code`; this predicate is the
// ONE place the step-up flow keys on it (the lease-preferred classification seam),
// so the raw "POST /api/sudo then retry" backend copy is intercepted into the
// SudoModal instead of rendering to the user.
export function isSudoRequired(error: unknown): boolean {
  return getErrorStatus(error) === 403 && getErrorCode(error) === 'sudo_required';
}

// Thrown by `runWithSudo` when the user dismisses the SudoModal (Cancel / Esc):
// the pending action was NOT performed. Consumers treat this as a benign abort
// (no error banner) via {@link isSudoCancelled} — distinct from a real failure.
export class SudoCancelledError extends Error {
  constructor() {
    super('sudo re-authentication cancelled');
    this.name = 'SudoCancelledError';
  }
}

export function isSudoCancelled(error: unknown): boolean {
  return error instanceof SudoCancelledError;
}

// #111 §5F — three legible states for the desktop sign-in path, so a transient
// "the local agent is still starting" never reads as a hard "can't connect" error.
export type ConnectionState =
  // 401 — no / expired backend session: show the sign-in form.
  | 'signed-out'
  // No HTTP response on DESKTOP: the local atlasd loopback proxy isn't up yet —
  // transient, retry/backoff (NOT an error).
  | 'daemon-starting'
  // 5xx (or, on WEB, no response): the Tizok backend is down / unreachable.
  | 'backend-unreachable'
  // Some other HTTP status — the caller's own copy applies.
  | 'http-error';

/**
 * Classify a failed request into one of the three legible sign-in states (+ a
 * passthrough). The DESKTOP vs WEB split matters only for "no HTTP response":
 * on desktop that means the loopback proxy isn't answering yet (daemon starting),
 * on web it's a real connectivity problem (backend unreachable).
 */
export function classifyConnection(error: unknown): ConnectionState {
  const status = getErrorStatus(error);
  if (status === 401) return 'signed-out';
  if (status === null) {
    return isDesktopRuntime() ? 'daemon-starting' : 'backend-unreachable';
  }
  if (status >= 502 && status <= 504) return 'backend-unreachable';
  return 'http-error';
}

export function connectionErrorMessage(): string {
  // Called when there is no HTTP response. On desktop, distinguish the transient
  // "daemon starting" state; on web, keep the existing copy byte-identical.
  if (classifyConnection(undefined) === 'daemon-starting') {
    return 'Starting the Tizok agent on your Mac… this usually takes a moment, then sign-in will work.';
  }
  return "We couldn't connect to Tizok. Check your internet connection and try again.";
}
